import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { temPapel, useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { GRUPOS, type ItemMenu, ITENS_MENU, itemPermitido } from "../lib/menu";

const semAcento = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Até quantas telas o início fica numa grade só, sem separar por grupo (a coletora do chão). */
const GRADE_UNICA_ATE = 12;

/** Botão do início: ícone e nome, sem texto embaixo (a descrição fica na dica do mouse). */
export function Atalho({ item, destaque = false }: { item: ItemMenu; destaque?: boolean }) {
  const Icone = item.icone;
  return (
    <Link to={item.to} title={item.texto}
      className={`flex min-h-14 items-center gap-3 rounded-lg border-2 px-3 py-2 transition-colors ${
        destaque ? "border-tinta bg-tinta text-white hover:bg-black" : "border-aco bg-chapa hover:border-tinta active:bg-concreto"}`}>
      <span className={`flex size-9 shrink-0 items-center justify-center rounded-md ${destaque ? "bg-faixa text-tinta" : "bg-concreto text-tinta"}`}>
        <Icone aria-hidden className="size-5" strokeWidth={2.25} />
      </span>
      <span className="line-clamp-2 min-w-0 font-sinal text-lg font-bold leading-tight">{item.titulo}</span>
    </Link>
  );
}

export function Grade({ itens, destaques = [] }: { itens: ItemMenu[]; destaques?: ItemMenu[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {itens.map((a) => <Atalho key={a.to} item={a} destaque={destaques.includes(a)} />)}
    </div>
  );
}

/** Início: as telas do usuário em botões de ícone e nome, com busca. As principais do papel vêm primeiro, em destaque. */
export default function Inicio() {
  const { perfil } = useAuth();
  const [busca, setBusca] = useState("");
  const visiveis = ITENS_MENU.filter((a) => !a.semMenu && itemPermitido(perfil, a));
  const principal = perfil?.papel === "admin" ? "lider" : perfil?.papel;
  const destaques = visiveis.filter((a) => principal && a.destaque?.includes(principal));
  const demais = visiveis.filter((a) => !destaques.includes(a));
  const estoquista = temPapel(perfil, "estoquista", "lider");
  const termo = semAcento(busca.trim());
  const achados = termo ? visiveis.filter((a) => semAcento(`${a.titulo} ${a.texto} ${a.grupo}`).includes(termo)) : [];

  const pendencias = useQuery({
    queryKey: ["inicio-pendencias"],
    enabled: estoquista,
    queryFn: async () => {
      const [inv, rep, cont] = await Promise.all([
        supabase.from("tarefas_inventario").select("id", { count: "exact", head: true }).eq("status", "pendente").is("inventario_id", null),
        supabase.from("tarefas_reposicao").select("id", { count: "exact", head: true }).eq("status", "pendente"),
        supabase.from("tarefas_inventario").select("id", { count: "exact", head: true }).eq("status", "pendente").not("inventario_id", "is", null),
      ]);
      return { rapidos: inv.count ?? 0, reposicoes: rep.count ?? 0, contagens: cont.count ?? 0 };
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-sinal text-2xl font-bold">Olá, {perfil?.nome.split(" ")[0]}</h1>

      <label className="relative block">
        <span className="sr-only">Buscar tela</span>
        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-aco-escuro" />
        <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Busca"
          className="min-h-12 w-full rounded-lg border-2 border-aco bg-chapa pl-10 pr-12 text-lg focus:border-tinta focus:outline-none" />
        {busca && (
          <button type="button" onClick={() => setBusca("")} aria-label="Limpar a busca"
            className="absolute right-1 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-md hover:bg-concreto">
            <X aria-hidden className="size-5" />
          </button>
        )}
      </label>

      {estoquista && !termo && pendencias.data && (pendencias.data.reposicoes > 0 || pendencias.data.contagens > 0 || pendencias.data.rapidos > 0) && (
        <div className="flex flex-wrap gap-2">
          {pendencias.data.reposicoes > 0 && (
            <Link to="/estoque/reposicao" className="rounded-lg bg-faixa px-4 py-3 font-semibold">
              {pendencias.data.reposicoes} reposição(ões) pendente(s)
            </Link>
          )}
          {pendencias.data.contagens > 0 && (
            <Link to="/estoque/contagem" className="rounded-lg bg-info-claro px-4 py-3 font-semibold text-info">
              {pendencias.data.contagens} posição(ões) para contar
            </Link>
          )}
          {pendencias.data.rapidos > 0 && (
            <Link to="/estoque" className="rounded-lg bg-alerta-claro px-4 py-3 font-semibold text-alerta">
              {pendencias.data.rapidos} posição(ões) com falta na coleta aguardando inventário
            </Link>
          )}
        </div>
      )}

      {termo ? (
        achados.length
          ? <Grade itens={achados} destaques={destaques} />
          : <p className="rounded-lg border-2 border-dashed border-aco px-4 py-6 text-center text-aco-escuro">Nenhuma tela com "{busca.trim()}".</p>
      ) : visiveis.length <= GRADE_UNICA_ATE ? (
        // poucas telas (chão): uma grade só, as principais do papel primeiro
        <Grade itens={[...destaques, ...demais]} destaques={destaques} />
      ) : (
        <>
          {destaques.length > 0 && <Grade itens={destaques} destaques={destaques} />}
          {GRUPOS.map((g) => {
            const itens = demais.filter((a) => a.grupo === g);
            if (!itens.length) return null;
            return (
              <section key={g} className="flex flex-col gap-2">
                <h2 className="font-sinal text-base font-semibold uppercase tracking-wide text-aco-escuro">{g}</h2>
                <Grade itens={itens} />
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
